import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type { Claim, LadderAnswer } from '../../components/Ladder/claims';
import type { LadderSuggestion } from '../../apis';
import { claimKey } from '../../components/Ladder/claims';
import { loadPins, persistPins } from '../helpers/persistenceHelpers';
import { pinDestination } from './reviewsSlice';
import type { LandingAgentTurn } from './landingAgentSlice';

/**
 * A number pinned from an answer (#183). Destination-aware (#189): with a
 * living review to take it, a pin lands in that review; otherwise it waits
 * in the queue Home shows until a review is built, which takes the queue.
 */
export interface Pin {
  id: string;
  key: string;
  /** The number pinned — or, for a WHAT NEXT suggestion (#188), a stand-in claim; `suggestion` carries the suggestion. */
  claim: Claim;
  suggestion?: LadderSuggestion | null;
  threadId: string | null;
  pinnedAt: string;
}

/** One pin per suggestion PER THREAD: the same watch can recur in another analysis and is a separate item there. */
export function suggestionPinKey(suggestion: LadderSuggestion, threadId: string | null): string {
  return `suggestion|${threadId || 'none'}|${suggestion.kind}:${suggestion.anchors?.[0] || 0}:${suggestion.text}`;
}

/** #1818: an answer whose defaults hold a reading item rests on a meaning no person has settled — its numbers are
 *  never pinned or added to a review's commentary (design-1818 §3.6; the review write refuses them too). */
export function restsOnAReading(ladder: LadderAnswer | null | undefined): boolean {
  return !!ladder?.defaults?.items?.some((i) => i.kind === 'reading');
}

function holdsKey(t: LandingAgentTurn, key: string): boolean {
  const what = (t.result?.ladder as LadderAnswer | null | undefined)?.what;
  return [...(what?.tiles || []), ...(what?.tables || []).flatMap((x) => x.claims || [])]
    .some((c) => c && claimKey(c) === key);
}

function holdsObject(node: any, claim: Claim, depth = 0): boolean {
  if (node === claim) return true;
  if (!node || typeof node !== 'object' || depth > 6) return false;
  return Object.values(node).some((v) => holdsObject(v, claim, depth + 1));
}

/** The answer on screen a number came from: the turn whose ladder holds this very claim, else the first turn holding
 *  a claim with its key (a key two answers share names no one answer — `claimRestsOnAReading` refuses then). */
export function answerOfClaim(turns: LandingAgentTurn[] | null | undefined, claim: Claim): LandingAgentTurn | null {
  const all = turns || [];
  return all.find((t) => holdsObject((t.result?.ladder as LadderAnswer | null | undefined)?.what, claim))
    || all.find((t) => holdsKey(t, claimKey(claim))) || null;
}

/** #1818: whether a number may rest on a reading (fail closed): the answer the caller names, the answer holding this
 *  very claim, or ANY answer on screen holding a claim with its key rests on a reading. A key is no answer's identity
 *  (the same metric, window and row can be in two answers), so it never chooses the cleaner one. */
export function claimRestsOnAReading(turns: LandingAgentTurn[] | null | undefined, claim: Claim,
                                     sessionId?: string | null): boolean {
  const all = turns || [];
  const key = claimKey(claim);
  return all.some((t) => ((sessionId && t.sessionId === sessionId) || holdsKey(t, key)
    || holdsObject((t.result?.ladder as LadderAnswer | null | undefined)?.what, claim))
    && restsOnAReading(t.result?.ladder as LadderAnswer | null | undefined));
}

export interface PinsSlice {
  pins: Pin[];
  /** `sessionId`: the answer the number came from, when the caller knows it (else the answer on screen holding it). */
  pinClaim: (claim: Claim, threadId: string | null, sessionId?: string | null) => Pin | undefined;
  /** Straight into the durable Home queue — for a pin whose chosen destination failed and must not be rerouted. */
  queuePin: (claim: Claim, threadId: string | null) => Pin | undefined;
  /** "Add to review commentary" (#188): a suggestion waits on Home until a review takes it (P1.1). */
  pinSuggestion: (suggestion: LadderSuggestion, threadId: string | null, sessionId?: string | null) => Pin | undefined;
  unpin: (pinId: string) => void;
  clearPins: () => void;
}

export const createPinsSlice: StateCreator<VoyagerState, [], [], PinsSlice> = (set, get) => {
  /** Into the durable Home queue, once per key. */
  const queue = (key: string, claim: Claim, threadId: string | null, suggestion: LadderSuggestion | null): Pin => {
    const existing = get().pins.find((p) => p.key === key);
    if (existing) return existing;
    const pin: Pin = { id: `pin_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, key, claim, ...(suggestion ? { suggestion } : {}),
      threadId, pinnedAt: new Date().toISOString() };
    const pins = [pin, ...get().pins];
    persistPins(pins);
    set({ pins } as Partial<VoyagerState>);
    return pin;
  };

  return {
  pins: loadPins(),

  queuePin: (claim, threadId) => {
    if (!claim.grounding && !claim.span) return undefined;
    return queue(claimKey(claim), claim, threadId, null);
  },

  pinClaim: (claim, threadId, sessionId) => {
    // A number that was not re-derived has no identity beyond its answer —
    // there is nothing durable to pin.
    if (!claim.grounding && !claim.span) return;
    if (claimRestsOnAReading(get().landingAgentTurns, claim, sessionId)) return;
    const answer = answerOfClaim(get().landingAgentTurns, claim);
    const session = sessionId || answer?.sessionId || null;
    const key = claimKey(claim);
    // Spans and comparisons are durable objects; a review cannot re-query them as metric values.
    if (claim.span || claim.statistic) return queue(key, claim, threadId, null);
    // A living review takes the pin (#189); the queue is for before one
    // exists — and for a pin the review could not take after all (the
    // request failed, the review was deleted meanwhile): it is kept here,
    // never lost.
    const dest = pinDestination(get().savedThreads, get().currentThreadId, claim.grounding?.perspective);
    if (dest) {
      get().pinClaimToReview(dest.id, claim, { thread_id: threadId, ...(session ? { session_id: session } : {}) }).then((section) => {
        if (!section) queue(key, claim, threadId, null);
      });
      return;
    }
    return queue(key, claim, threadId, null);
  },

  pinSuggestion: (suggestion, threadId, sessionId) => {
    if (sessionId && restsOnAReading(get().landingAgentTurns.find((t) => t.sessionId === sessionId)?.result?.ladder)) return;
    const key = suggestionPinKey(suggestion, threadId);
    // A suggestion was derived in its thread's perspective.
    const from = get().savedThreads.find((t) => t.id === threadId)?.perspectiveId || null;
    const claim: Claim = { id: `suggestion:${suggestion.anchors?.[0] || 'none'}`, label: suggestion.text, value: suggestion.projection?.value ?? null,
      format: null, confidence: 1, confidence_basis: 'suggestion', verdict: 'derived', grounding: null, caveats: [] } as any;
    const dest = pinDestination(get().savedThreads, get().currentThreadId, from);
    if (dest) {
      get().pinSuggestionToReview(dest.id, suggestion, { thread_id: threadId, ...(sessionId ? { session_id: sessionId } : {}) }).then((section) => {
        if (!section) queue(key, claim, threadId, suggestion);
      });
      return;
    }
    return queue(key, claim, threadId, suggestion);
  },

  unpin: (pinId) => {
    const pins = get().pins.filter((p) => p.id !== pinId);
    persistPins(pins);
    set({ pins } as Partial<VoyagerState>);
  },

  clearPins: () => {
    persistPins([]);
    set({ pins: [] } as Partial<VoyagerState>);
  },
  };
};
